import type { Monaco } from '@monaco-editor/react';

export const STRUCTURIZR_LANGUAGE_ID = 'structurizr';

// Token type constants
const COMMENT_TOKEN = 'comment';
const STRING_TOKEN = 'string';
const ESCAPE_ACTION = '@escapes';
const POP_ACTION = '@pop';
const COMMENT_ACTION = '@comment';
const STRING_ACTION = '@string';

/**
 * Register Structurizr DSL language with Monaco editor
 * Provides syntax highlighting for keywords, strings, comments, and relationships
 */
export const registerStructurizrLanguage = (monaco: Monaco): void => {
  // Check if language is already registered
  const languages = monaco.languages.getLanguages();
  if (languages.some((lang: { id: string }) => lang.id === STRUCTURIZR_LANGUAGE_ID)) {
    return;
  }

  // Register the language
  monaco.languages.register({ id: STRUCTURIZR_LANGUAGE_ID });

  // Define tokens for syntax highlighting
  monaco.languages.setMonarchTokensProvider(STRUCTURIZR_LANGUAGE_ID, {
    keywords: [
      'workspace',
      'model',
      'views',
      'styles',
      'configuration',
      'properties',
      'perspectives',
      'person',
      'softwareSystem',
      'container',
      'component',
      'deploymentEnvironment',
      'deploymentGroup',
      'deploymentNode',
      'infrastructureNode',
      'softwareSystemInstance',
      'containerInstance',
      'element',
      'relationship',
      'systemContext',
      'containerView',
      'componentView',
      'dynamicView',
      'deploymentView',
      'filteredView',
      'systemLandscape',
      'custom',
      'image',
      'theme',
      'themes',
      'branding',
      'terminology',
      'scope',
      'extends',
      'include',
      'exclude',
      'autoLayout',
      'animation',
      'default',
      'description',
      'technology',
      'tags',
      'url',
      'group',
      'enterprise',
    ],

    directives: ['!include', '!docs', '!adrs', '!constant'],

    escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4})/,

    tokenizer: {
      root: [
        // Comments - single line
        [/\/\/.*$/, COMMENT_TOKEN],
        // Comments - multi line
        [/\/\*/, COMMENT_TOKEN, COMMENT_ACTION],

        // Directives (e.g., !include, !docs)
        [/![a-zA-Z]+/, 'keyword.directive'],

        // Relationships (->)
        [/->/, 'operator.relationship'],

        // Strings - double quoted
        [/"([^"\\]|\\.)*$/, 'string.invalid'], // non-terminated string
        [/"/, STRING_TOKEN, STRING_ACTION],

        // Identifiers and keywords
        [
          /[a-zA-Z_]\w*/,
          {
            cases: {
              '@keywords': 'keyword',
              '@directives': 'keyword.directive',
              '@default': 'identifier',
            },
          },
        ],

        // Numbers
        [/\d+/, 'number'],

        // Whitespace
        [/[ \t\r\n]+/, 'white'],

        // Braces
        [/[{}]/, 'delimiter.bracket'],
      ],

      comment: [
        [/[^/*]+/, COMMENT_TOKEN],
        [/\*\//, COMMENT_TOKEN, POP_ACTION],
        [/[/*]/, COMMENT_TOKEN],
      ],

      string: [
        [/[^\\"]+/, STRING_TOKEN],
        [/@escapes/, 'string.escape'],
        [/\\./, 'string.escape.invalid'],
        [/"/, STRING_TOKEN, POP_ACTION],
      ],
    },
  });

  // Define language configuration
  monaco.languages.setLanguageConfiguration(STRUCTURIZR_LANGUAGE_ID, {
    comments: {
      lineComment: '//',
      blockComment: ['/*', '*/'],
    },
    brackets: [
      ['{', '}'],
      ['[', ']'],
      ['(', ')'],
    ],
    autoClosingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
    ],
    surroundingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
    ],
    folding: {
      markers: {
        start: /^\s*\{/,
        end: /^\s*\}/,
      },
    },
    indentationRules: {
      increaseIndentPattern: /.*\{\s*$/,
      decreaseIndentPattern: /^\s*\}/,
    },
  });
};

/**
 * Define custom theme colors for Structurizr DSL
 * Enhances visibility of C4-specific elements
 */
export const defineStructurizrTheme = (monaco: Monaco): void => {
  monaco.editor.defineTheme('structurizr-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'keyword', foreground: '0077B6', fontStyle: 'bold' },
      { token: 'keyword.directive', foreground: 'E85D04', fontStyle: 'bold' },
      { token: 'operator.relationship', foreground: 'D62828', fontStyle: 'bold' },
      { token: 'string', foreground: '2D6A4F' },
      { token: 'comment', foreground: '6C757D', fontStyle: 'italic' },
      { token: 'identifier', foreground: '212529' },
      { token: 'number', foreground: '7B2CBF' },
      { token: 'delimiter.bracket', foreground: '495057' },
    ],
    colors: {
      'editor.background': '#FFFFFF',
      'editor.foreground': '#212529',
      'editor.lineHighlightBackground': '#F8F9FA',
      'editorLineNumber.foreground': '#ADB5BD',
      'editorLineNumber.activeForeground': '#495057',
    },
  });
};

/**
 * Monaco snippet templates for Structurizr DSL auto-completion.
 * These strings use Monaco's snippet syntax where ${n:placeholder} defines
 * tab stops - they are NOT JavaScript template literals.
 */

// Common snippet suffixes
const VIEW_SNIPPET_SUFFIX = '{\n\tinclude *\n\tautoLayout\n}';

// NOSONAR: All strings below use Monaco snippet syntax (${n:placeholder}),
// not JavaScript template literals. SonarQube S3786 is a false positive.
const SNIPPET_TEMPLATES = {
  workspace: 'workspace "${1:name}" "${2:description}" {\n\t$0\n}', // NOSONAR
  model: 'model {\n\t$0\n}',
  views: 'views {\n\t$0\n}',
  person: '${1:identifier} = person "${2:name}" "${3:description}"', // NOSONAR
  softwareSystem:
    '${1:identifier} = softwareSystem "${2:name}" "${3:description}" {\n\t$0\n}', // NOSONAR
  container:
    '${1:identifier} = container "${2:name}" "${3:description}" "${4:technology}"', // NOSONAR
  component:
    '${1:identifier} = component "${2:name}" "${3:description}" "${4:technology}"', // NOSONAR
  systemContextView:
    // eslint-disable-next-line prefer-template
    'systemContext ${1:softwareSystem} "${2:key}" "${3:description}" ' + // NOSONAR
    VIEW_SNIPPET_SUFFIX,
  containerView:
    // eslint-disable-next-line prefer-template
    'container ${1:softwareSystem} "${2:key}" "${3:description}" ' + // NOSONAR
    VIEW_SNIPPET_SUFFIX,
  componentView:
    // eslint-disable-next-line prefer-template
    'component ${1:container} "${2:key}" "${3:description}" ' + // NOSONAR
    VIEW_SNIPPET_SUFFIX,
  include: 'include ${1:*}', // NOSONAR
  exclude: 'exclude ${1:element}', // NOSONAR
  styles: 'styles {\n\t$0\n}',
  elementStyle:
    'element "${1:tag}" {\n\tbackground ${2:#438DD5}\n\tcolor ${3:#ffffff}\n\tshape ${4:RoundedBox}\n}', // NOSONAR
  relationshipStyle:
    'relationship "${1:tag}" {\n\tthickness ${2:2}\n\tcolor ${3:#707070}\n}', // NOSONAR
  relationshipArrow: '-> ${1:target} "${2:description}"', // NOSONAR
};
 

/**
 * Register completion provider for Structurizr DSL
 * Provides intelligent auto-complete suggestions
 */
export const registerStructurizrCompletionProvider = (monaco: Monaco): void => {
  monaco.languages.registerCompletionItemProvider(STRUCTURIZR_LANGUAGE_ID, {
    provideCompletionItems: (
      model: Monaco['editor']['ITextModel'],
      position: Monaco['IPosition'],
    ) => {
      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      const suggestions = [
        // Top-level elements
        {
          label: 'workspace',
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: SNIPPET_TEMPLATES.workspace,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define a new workspace',
          range,
        },
        {
          label: 'model',
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: SNIPPET_TEMPLATES.model,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define the model section',
          range,
        },
        {
          label: 'views',
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: SNIPPET_TEMPLATES.views,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define the views section',
          range,
        },
        // Model elements
        {
          label: 'person',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: SNIPPET_TEMPLATES.person,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define a person/actor in the system',
          range,
        },
        {
          label: 'softwareSystem',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: SNIPPET_TEMPLATES.softwareSystem,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define a software system',
          range,
        },
        {
          label: 'container',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: SNIPPET_TEMPLATES.container,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define a container within a software system',
          range,
        },
        {
          label: 'component',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: SNIPPET_TEMPLATES.component,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define a component within a container',
          range,
        },
        // Views
        {
          label: 'systemContext',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: SNIPPET_TEMPLATES.systemContextView,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define a System Context view',
          range,
        },
        {
          label: 'containerView',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: SNIPPET_TEMPLATES.containerView,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define a Container view',
          range,
        },
        {
          label: 'componentView',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: SNIPPET_TEMPLATES.componentView,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define a Component view',
          range,
        },
        // Other keywords
        {
          label: 'autoLayout',
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: 'autoLayout',
          documentation: 'Enable automatic layout for the view',
          range,
        },
        {
          label: 'include',
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: SNIPPET_TEMPLATES.include,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Include elements in the view',
          range,
        },
        {
          label: 'exclude',
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: SNIPPET_TEMPLATES.exclude,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Exclude elements from the view',
          range,
        },
        {
          label: 'styles',
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: SNIPPET_TEMPLATES.styles,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define styles section',
          range,
        },
        {
          label: 'element',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: SNIPPET_TEMPLATES.elementStyle,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define element style',
          range,
        },
        {
          label: 'relationship',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: SNIPPET_TEMPLATES.relationshipStyle,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define relationship style',
          range,
        },
        // Relationship
        {
          label: '->',
          kind: monaco.languages.CompletionItemKind.Operator,
          insertText: SNIPPET_TEMPLATES.relationshipArrow,
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Define a relationship between elements',
          range,
        },
      ];

      return { suggestions };
    },
  });
};
